import React, { Fragment, useState, useEffect } from 'react'
import Button from '@material-ui/core/Button'
import FilledInput from '@material-ui/core/FilledInput'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import Grid from '@material-ui/core/Grid'
import InputAdornment from '@material-ui/core/InputAdornment'
import InputLabel from '@material-ui/core/InputLabel'
import Paper from '@material-ui/core/Paper'
import Switch from '@material-ui/core/Switch'
import { makeStyles } from '@material-ui/core/styles'
import {
  ArrayInput,
  BooleanInput,
  DateTimeInput,
  FileField,
  FileInput,
  FormDataConsumer,
  FormWithRedirect,
  SaveButton,
  SelectInput,
  TextInput,
  minValue,
  required,
  useGetOne,
  useMutation,
  useNotify,
  useRefresh,
  useTranslate,
  useVersion,
} from 'react-admin'
import { Field } from 'react-final-form'
import InventoryItemPicker from '../components/InventoryItemPicker'
import FormItemList from '../components/FormItemList'
import AddButton from '../components/AddButton'
import SendIcon from '@material-ui/icons/Send'
import { useCurrentGame, useGameFeatures } from '../utils/games'
import MessageBodyInput from './MessageBodyInput'
import { describeError } from '../utils/errors'
import EnvironmentIcon from '../components/EnvironmentIcon'
import { Card, CardContent } from '@material-ui/core'

/** @typedef {import('admintool-api/v2/v2_common_inventory').GetItemTypesResponse} GetItemTypesResponse */
/** @typedef {import('react-admin').GetOneResult<GetItemTypesResponse>} ItemTypesGetOneResult */

const recipientTypes = [
  { id: 'csv', name: 'CSV file', },
  { id: 'ids', name: 'List of player IDs', },
  { id: 'all', name: 'All Players', },
]

const emptyInventoryItem = { id: '', section: '', amount: 1 }

const initialValues = {
  recipientsType: 'csv',
  csvFile: null,
  playerIds: '',
  message: '',
  sendTime: '',
  doesExpire: false,
  expireInDays: 30,
  agentNote: '',
  supportTicket: '',
  items: [],
  deduplicate: false,
  mustClaim: true,
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
  },
  customControl: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
  },
  labeledSwitch: {
    marginTop: '14px', // TextInput top padding 23px - Switch button top padding 9 px
  },
  flexStatic: {
    flex: '0 0 auto',
  },
  flexGrow: {
    flex: '1 1 auto',
  },
  fileInput: {
    margin: 0,
  },
  inlineNotification: {
    padding: theme.spacing(1),
    background: '#fff3e0',
    width: 'max-content',
  },
  inlineNotificationProd: {
    border: 'thick double red',
    padding: theme.spacing(1),
    background: '#fff3e0',
    width: 'max-content',
  },
  error: {
    padding: theme.spacing(1),
    background: '#ffe0e0',
    width: 'max-content',
  },
}))

function addDays(date, days) {
  const newDate = new Date(date)
  newDate.setDate(date.getDate() + parseInt(days, 10))
  return newDate
}

const validateSendTime = () => (value, allValues) => {
  if (allValues.doesExpire && !value) {
    return 'Required for expiring messages'
  }
  if (value && isNaN(value.valueOf())) {
    return 'Invalid date'
  }
  if (value && value - Date.now() < 60 * 60 * 1000) {
    return 'Send time is in the past'
  }
}

const fileProvided = () => value => {
  if (!value) {
    return 'A file is required'
  }
}

const AnnouncementForm = props => {
  const notify = useNotify()
  const refresh = useRefresh()
  const classes = useStyles()
  const version = useVersion()
  const translate = useTranslate()
  const currentGame = useCurrentGame()
  const gameFeatures = useGameFeatures()
  /** @type ItemTypesGetOneResult */
  const itemTypes = useGetOne('itemtypes', 'latest')
  const [send, { loading: sending }] = useMutation()
  const [duplicates, setDuplicates] = useState([])
  const [confirmMode, setConfirmMode] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [announcementId, setAnnouncementId] =  useState(null)

  function handleFormSubmit(values) {
    const announcement = {
      message: {
        html: values.message,
        title: values.title,
        description: values.description,
        rewards: values.items.map(item => ({
          section: item.section,
          item: {
            typeId: item.id,
            count: item.amount,
          },
        })),
      },
      group: {
        allPlayers: values.recipientsType === 'all',
        csv: values.recipientsType === 'csv' ? values.csvFile : null,
        playerIds: values.recipientsType === 'ids' ? values.playerIds.split(/[,\s\n]/).filter(p => !!p) : null,
        automaticallyDeduplicate: values.deduplicate,
      },
      appearTime: values.sendTime || null,
      expireTime: values.doesExpire ? addDays(values.sendTime, values.expireInDays) : null,
      agentNote: values.agentNote,
      supportTicket: values.supportTicket,
    }
    if (gameFeatures['donation/claim'] && values.items.length > 0) {
      announcement.message.mustClaimRewards = values.mustClaim
    }

    send(
      {
        type: 'create',
        resource: 'messaging/announcement',
        payload: {
          data: announcement,
        },
      },
      {
        onSuccess: result => {
          // TODO: this should not need to care about the exact status code
          if (result.response.status === 202) {
            setAnnouncementId(result.data.id)
          } else {
            notify('Announcement sent successfully')
            setConfirmMode(false)
            setDuplicates([])
            setErrorMessage('')
            setAnnouncementId(null)
            refresh()
          }
        },
        onFailure: error => {
          if (error.body && error.body.group && error.body.group.duplicatedIds && error.body.group.duplicatedIds.length > 0) {
            setDuplicates(error.body.group.duplicatedIds)
          } else {
            setConfirmMode(false)
            setErrorMessage(describeError(error))
          }
        },
      })
  }

  return <Card><CardContent>
    <FormWithRedirect
      {...props}
      save={handleFormSubmit}
      saving={sending}
      initialValues={initialValues}
      version={version}
      render={formProps => {
        if (confirmMode && formProps.invalid) setConfirmMode(false)
        return <Grid container spacing={4}>
          <Grid item container xs={12} md={6} spacing={1} alignContent='flex-start'>
            <Grid item xs={12} lg={5}>
              <SelectInput
                choices={recipientTypes}
                source='recipientsType'
                label='Recipient'
                fullWidth
                validate={required()}
              />
            </Grid>
            <Grid item xs={12} lg={7}>
              <FormDataConsumer>
                {({ formData, ...rest }) => {
                  switch (formData.recipientsType) {
                    case 'csv':
                      return <FileInput
                        source='csvFile'
                        label='' // Setting no label at all gives auto-generated one, but empty string hides the label
                        multiple={false}
                        accept='.csv,.txt'
                        placeholder={<p>Drop or click to select .csv or .txt file with player IDs</p>}
                        className={classes.fileInput}
                        validate={fileProvided()}
                        {...rest}
                      >
                        <FileField source='src' title='title' />
                      </FileInput>
                    case 'ids':
                      return <TextInput
                        source='playerIds'
                        label='Player IDs'
                        multiline rows={2} rowsMax={Infinity}
                        fullWidth
                        validate={required()}
                        {...rest}
                      />
                    case 'all':
                      return null
                    default:
                      return <p>Internal error: bad value for recipientsType</p>
                    }
                }}
              </FormDataConsumer>
            </Grid>

            <Grid item xs={12}>
              <TextInput
                source='title'
                label='Message title'
                fullWidth
                validate={required()}
              />
            </Grid>

            <Grid item xs={12}>
              <TextInput
                source='description'
                label='Short description for the message'
                fullWidth
                validate={required()}
              />
            </Grid>

            <Grid item xs={12}>
              <MessageBodyInput
                source='message'
                label='Message'
                fullWidth
                validate={required()}
              />
            </Grid>

            <Grid item xs={12} sm={5} md={12} lg={5}>
              <DateTimeInput
                source='sendTime'
                label='Send message on'
                fullWidth
                validate={validateSendTime()}
              />
            </Grid>
            <Grid item xs={12} sm={7} md={12} lg={7} className={classes.customControl} >
              <FormControl margin='dense' className={classes.flexStatic}>
                <InputLabel htmlFor='expiration-time' shrink={true} variant='filled'>Message&nbsp;expiration&nbsp;time</InputLabel>
                <Field name='doesExpire' type='checkbox'>
                  {({ input }) => <Switch className={classes.labeledSwitch} id='expiration-time' value={input.value} onChange={input.onChange} />}
                </Field>
              </FormControl>
              <FormControl margin='dense' className={classes.flexGrow} >
                <FormDataConsumer>
                  {({ formData, ...rest }) =>
                    formData.doesExpire ?
                      <Field name='expireInDays' validate={minValue(1)}>
                        {({ input, meta }) => <Fragment>
                          <FilledInput
                            margin='dense'
                            type='number'
                            value={input.value}
                            onChange={input.onChange}
                            endAdornment={<InputAdornment position='end'>days</InputAdornment>}
                            error={!!meta.error}
                            {...rest}
                          />
                          {meta.error ? <FormHelperText error>{translate(meta.error.message, meta.error.args)}</FormHelperText> : null}
                        </Fragment>
                        }
                      </Field> : null
                  }
                </FormDataConsumer>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextInput source='agentNote' label='Support agent notes (optional)' multiline fullWidth rows={2} rowsMax={Infinity} />
            </Grid>
            <Grid item xs={12}>
              <TextInput source='supportTicket' label='Support ticket no. (optional)' fullWidth />
            </Grid>
          </Grid>

          <Grid item container xs={12} md={6} alignContent='flex-start'>
            <ArrayInput source='items' label='Reward items'>
              <FormItemList add={<AddButton text='Add reward item' />} emptyItem={emptyInventoryItem}>
                <InventoryItemPicker
                  itemTypes={itemTypes.loaded && itemTypes.data ? itemTypes.data.itemTypes : null}
                />
              </FormItemList>
            </ArrayInput>

            {gameFeatures['donation/claim'] && (
              <FormDataConsumer>
                {({ formData, ...rest }) => (
                  formData.items.length > 0 ?
                    <BooleanInput
                      label='Player must claim rewards'
                      source='mustClaim'
                      {...rest}
                    /> : null
                )}
              </FormDataConsumer>
            )}
          </Grid>

          {duplicates.length > 0 ?
            <Grid item xs={12}>
              <Paper className={classes.inlineNotification} >
                <p>There {duplicates.length === 1 ? 'was a' : ('were ' + duplicates.length)} {}
                  player {duplicates.length === 1 ? 'ID' : 'IDs'} that appeared more than once.</p>
                <p>Duplicated IDs: {duplicates.join(', ')}</p>
                <BooleanInput label='Automatically deduplicate' source='deduplicate' />
              </Paper>
            </Grid>
            : null}
          <Grid item xs={12}>
            <Button
              variant='contained'
              color='primary'
              startIcon={<SendIcon />}
              disabled={formProps.pristine || formProps.invalid || confirmMode}
              onClick={() => { setConfirmMode(true); setErrorMessage('') }}
            >
              Send to {currentGame.id}-{currentGame.env}
            </Button>
            {formProps.pristine || formProps.invalid ? <span>Some required fields are empty</span> : null}
          </Grid>
          {errorMessage && 
            <Grid item xs={12}>
              <Paper className={classes.error}>
                <p>Sending the message failed</p>
                <p>{errorMessage}</p>
              </Paper>
            </Grid>
          }
          {confirmMode &&
            <Grid item xs={12}>
              <Paper
                className={currentGame.env === 'prod' ?
                  classes.inlineNotificationProd :
                  classes.inlineNotification}
              >
                <FormDataConsumer>
                  {({ formData }) => (<>
                    <p><strong>{currentGame.id}-{currentGame.env}</strong>
                      <EnvironmentIcon env={currentGame.env} />
                    </p>
                    <p>You are about to send an announcement
                      titled <strong>{formData.title}</strong> to <strong>{
                      formData.recipientsType === 'all' ? 'all players' :
                      formData.recipientsType === 'ids' ? `list of ${formData.playerIds.split(/[,\s\n]/).filter(p => !!p).length} players` :
                      formData.recipientsType === 'csv' ? `players in file "${formData.csvFile ? formData.csvFile.title : '(file missing)'}"` :
                      '...somewhere? An internal error occurred'
                    }</strong>. This action can not be undone.</p>
                    <p>This message contains <strong>{
                      formData.items.length === 0 ? 'no rewards' :
                      formData.items.length === 1 ? 'one reward' :
                      `${formData.items.length} rewards`
                    }</strong>.</p>
                  </>)}
                </FormDataConsumer>
                { announcementId ?
                  <WatchStatus id={announcementId} /> :
                  <SaveButton
                    submitOnEnter
                    label='Confirm send'
                    saving={formProps.saving}
                    handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                    icon={<SendIcon />}
                  />
                }
              </Paper>
            </Grid>
          }
        </Grid>
      }} />
  </CardContent></Card>
}

export default AnnouncementForm

const WatchStatus = ({id}) => {
  const [timer, setTimer] = useState(0)
  const [running, setRunning] = useState(true)
  const status = useGetOne('messaging/announcement', id, {version: timer})

  useEffect(() => {
    if (running) setTimeout(() => setTimer(t => t + 1), 1000)
  }, [timer, running])

  if (!status.data) return null
  if (running && status.data.finishTime) setRunning(false)

  return <>
    {status.data.enqueueTime && <p>Server received the announcement: {status.data.enqueueTime}</p>}
    {status.data.startTime && <p>Sending messages started: {status.data.startTime}</p>}
    {status.data.finishTime && <p>Sending messages completed: {status.data.finishTime}</p>}
    {status.data.fractionDone && <p>Progress: {status.data.fractionDone * 100}&nbsp;% of all messages sent</p>}
  </>
}
